import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, deleteUser, signOut, createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { firebaseConfig } from './firebase';

/**
 * Attempts to delete a user from Firebase Auth using a "best effort" approach.
 * Since the Client SDK requires the user to be signed in to delete their account,
 * this function tries to sign in as the user using the default password 'egurukul@123456'.
 * 
 * Works ONLY if the user has not changed their password.
 * 
 * @param {string} email The email of the user to delete.
 */
export const deleteUserFromFirebaseBestEffort = async (email) => {
    if (!email) return;

    const defaultPasswords = ["egurukul@123456"];
    const appName = `deleteApp-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    let secondaryApp = null;

    try {
        secondaryApp = initializeApp(firebaseConfig, appName);
        const secondaryAuth = getAuth(secondaryApp);

        let userCredential = null;
        let lastError = null;

        // Try to sign in with each default password
        for (const password of defaultPasswords) {
            try {
                userCredential = await signInWithEmailAndPassword(secondaryAuth, email, password);
                if (userCredential) break;
            } catch (err) {
                lastError = err;
            }
        }

        if (userCredential?.user) {
            await deleteUser(userCredential.user);
            console.log(`DEBUG: Successfully deleted user ${email} from Firebase Auth.`);
        } else if (lastError) {
            throw lastError;
        }

        await signOut(secondaryAuth);
        await deleteApp(secondaryApp);
    } catch (error) {
        if (secondaryApp) {
            try { await deleteApp(secondaryApp); } catch (e) { /* ignore */ }
        }
        
        // If user doesn't exist, we consider it a "success" for removal purposes
        if (error.code === 'auth/user-not-found') {
            console.log(`DEBUG: User ${email} already doesn't exist in Firebase.`);
            return;
        }
        
        // Re-throw other errors so the caller knows deletion failed (e.g. wrong password)
        console.warn(`DEBUG: Failed to delete user ${email} from Firebase Auth.`, error.code);
        throw error;
    }
};

/**
 * Creates a user in Firebase Auth using a secondary app instance (to avoid signing out the current user).
 * 
 * @param {string} email The email of the user to create.
 * @param {string} password The password for the new user.
 */
export const createUserInFirebase = async (email, password) => {
    if (!email || !password) return;

    const appName = `createApp-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    let secondaryApp = null;

    try {
        secondaryApp = initializeApp(firebaseConfig, appName);
        const secondaryAuth = getAuth(secondaryApp);

        const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
        console.log(`DEBUG: Successfully created user ${email} in Firebase Auth.`);

        await signOut(secondaryAuth);
        await deleteApp(secondaryApp);
        return userCredential.user;
    } catch (error) {
        console.warn(`DEBUG: Failed to create user ${email} in Firebase Auth.`, error.code, error.message);
        if (secondaryApp) {
            try {
                await deleteApp(secondaryApp);
            } catch (e) {
                // Ignore cleanup errors
            }
        }
        throw error;
    }
};
/**
 * Checks if a user exists in Firebase Auth by attempting to sign in with a dummy password.
 * This is a common pattern for checking existence with the Client SDK.
 * 
 * @param {string} email The email of the user to check.
 * @returns {Promise<boolean>} True if the user exists, false otherwise.
 */
export const checkFirebaseUserStatus = async (email) => {
    if (!email) return false;

    const appName = `checkApp-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    let secondaryApp = null;

    try {
        secondaryApp = initializeApp(firebaseConfig, appName);
        const secondaryAuth = getAuth(secondaryApp);

        // Attempt sign-in with a dummy password
        await signInWithEmailAndPassword(secondaryAuth, email, "DUMMY_PASSWORD_CHECK_123");
        
        // If we somehow sign in (highly unlikely), return true
        await signOut(secondaryAuth);
        await deleteApp(secondaryApp);
        return true;
    } catch (error) {
        if (secondaryApp) {
            try { await deleteApp(secondaryApp); } catch (e) { /* ignore */ }
        }

        // auth/wrong-password means the user EXISTS
        if (error.code === 'auth/wrong-password' || error.code === 'auth/too-many-requests') {
            return true;
        }

        // auth/user-not-found means the user DOES NOT exist
        if (error.code === 'auth/user-not-found') {
            return false;
        }

        // For other errors, assume not found or re-throw if critical
        console.warn(`DEBUG: Firebase status check error for ${email}:`, error.code);
        return false;
    }
};

/**
 * Sends a password reset email to the given address.
 * 
 * @param {string} email The email to send the reset link to.
 */
export const sendResetEmail = async (email) => {
    if (!email) return { success: false, error: 'No email provided' };
    try {
        const auth = getAuth();
        await sendPasswordResetEmail(auth, email);
        return { success: true };
    } catch (error) {
        console.warn(`DEBUG: Failed to send reset email to ${email}`, error.code);
        return { success: false, error: error.message };
    }
};
